import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  CheckCircle as ValidIcon,
  Cancel as InvalidIcon,
  Schedule as PendingIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../utils/api';

console.log('🔍 EmailHub: File loaded successfully');

interface Lead {
  _id: string;
  leadId: string;
  companyId: string;
  companyName: string;
  originalData: {
    'First Name'?: string;
    'Last Name'?: string;
    'Company Name'?: string;
    'Work Email'?: string;
    'Work Phone'?: string;
    [key: string]: any;
  };
  emailValidationStatus?: 'valid' | 'invalid' | 'pending';
  emailValidationDate?: string;
  createdAt: string;
}

interface ValidationStats {
  total: number;
  valid: number;
  invalid: number;
  pending: number;
}

const EmailHub: React.FC = () => {
  console.log('🔍 EmailHub: Component function called');
  
  const theme = useTheme();
  const { user } = useAuth();
  
  console.log('🔍 EmailHub: Component initialized');
  console.log('🔍 EmailHub: User:', user);
  console.log('🔍 EmailHub: Auth token exists:', !!sessionStorage.getItem('token'));
  console.log('🔍 EmailHub: Component is rendering!');
  
  const [leads, setLeads] = useState<Lead[]>([]);
  const [filteredLeads, setFilteredLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(false);
  const [validationProgress, setValidationProgress] = useState(0);
  const [stats, setStats] = useState<ValidationStats>({ total: 0, valid: 0, invalid: 0, pending: 0 });
  const [filterStatus, setFilterStatus] = useState<'all' | 'valid' | 'invalid' | 'pending'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingInvalid, setDeletingInvalid] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [validationMessage, setValidationMessage] = useState('');
  const [isValidationRunning, setIsValidationRunning] = useState(false);
  
  // Use refs to track polling intervals and avoid memory leaks
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Fetch all leads for email validation
  const fetchLeads = async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      console.log('🔍 EmailHub: Starting to fetch leads...');
      console.log('🔍 EmailHub: API base URL:', api.defaults.baseURL);
      console.log('🔍 EmailHub: Auth token:', !!sessionStorage.getItem('token'));
      console.log('🔍 EmailHub: Making API call to: /admin/email-hub/leads');
      console.log('🔍 EmailHub: API instance:', api);
      
      const response = await api.get('/api/admin/email-hub/leads');
      console.log('🔍 EmailHub: API response received:', response);
      
      const leadsData = response.data?.data || [];
      console.log('🔍 EmailHub: Leads data:', leadsData);
      
      // Ensure leadsData is an array
      if (Array.isArray(leadsData)) {
        console.log(`📊 EmailHub: Found ${leadsData.length} leads`);
        setLeads(leadsData);
        setFilteredLeads(leadsData);
        calculateStats(leadsData);
      } else {
        console.warn('⚠️ EmailHub: API response data is not an array:', leadsData);
        setLeads([]);
        setFilteredLeads([]);
        calculateStats([]);
      }
    } catch (error: any) {
      console.error('❌ EmailHub: Error fetching leads:', error);
      console.error('❌ EmailHub: Error details:', {
        message: error.message,
        response: error.response,
        status: error.response?.status,
        data: error.response?.data
      });
      
      // Check if it's an authentication error
      if (error.response?.status === 401) {
        setError('Authentication required. Please log in as a super admin.');
      } else if (error.response?.status === 403) {
        setError('Access denied. Super admin privileges required.');
      } else if (error.response?.data?.message) {
        setError(`API Error: ${error.response.data.message}`);
      } else if (error.message) {
        setError(`Error: ${error.message}`);
      } else {
        setError('Failed to fetch leads. Please check your connection and try again.');
      }
      
      setLeads([]);
      setFilteredLeads([]);
      calculateStats([]);
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  // Calculate validation statistics
  const calculateStats = (leadsData: Lead[]): ValidationStats => {
    if (!leadsData || !Array.isArray(leadsData)) {
      const emptyStats = { total: 0, valid: 0, invalid: 0, pending: 0 };
      setStats(emptyStats);
      return emptyStats;
    }
    
    const stats = leadsData.reduce((acc, lead) => {
      acc.total++;
      const status = lead.emailValidationStatus || 'pending';
      acc[status]++;
      return acc;
    }, { total: 0, valid: 0, invalid: 0, pending: 0 });
    setStats(stats);
    return stats;
  };

  // Filter leads based on status and search term
  useEffect(() => {
    if (!leads || !Array.isArray(leads)) {
      setFilteredLeads([]);
      return;
    }

    let filtered = leads;

    // Filter by status
    if (filterStatus !== 'all') {
      filtered = filtered.filter(lead => 
        (lead.emailValidationStatus || 'pending') === filterStatus
      );
    }

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(lead => 
        lead.originalData?.['First Name']?.toLowerCase().includes(term) ||
        lead.originalData?.['Last Name']?.toLowerCase().includes(term) ||
        lead.originalData?.['Company Name']?.toLowerCase().includes(term) ||
        lead.originalData?.['Work Email']?.toLowerCase().includes(term) ||
        lead.originalData?.['Work Phone']?.toLowerCase().includes(term) ||
        lead.companyName?.toLowerCase().includes(term)
      );
    }

    setFilteredLeads(filtered);
  }, [leads, filterStatus, searchTerm]);

  // Start email validation process with improved polling
  const startValidation = async () => {
    try {
      setValidating(true);
      setValidationProgress(0);
      setValidationMessage('Starting email validation...');
      setError(null);
      setSuccess(null);
      
      // Start the validation on the backend using api instance
      const response = await api.post('/api/admin/email-hub/validate', {
        apiKey: 'vk_prod_d14507a236fc194b78603836'
      });
      
      const data = response.data;
      
      if (data.alreadyRunning) {
        console.log('✅ Validation already running in background');
        setSuccess('Email validation is already running in the background');
      } else {
        console.log('✅ Validation started successfully');
        setSuccess('Email validation started successfully');
      }
      
      // Start polling with exponential backoff
      let pollCount = 0;
      const startPolling = () => {
        // Clear any existing interval
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
        }
        
        pollIntervalRef.current = setInterval(async () => {
          try {
            pollCount++;
            console.log(`🔄 Polling for updates (attempt ${pollCount})...`);
            
            // Fetch fresh leads silently (no loading spinner)
            const response = await api.get('/api/admin/email-hub/leads');
            const leadsData = response.data?.data || [];
            
            if (Array.isArray(leadsData)) {
              // Update leads silently without showing loading spinner
              setLeads(leadsData);
              setFilteredLeads(leadsData);
              
              // Calculate fresh stats from the new data
              const freshStats = calculateStats(leadsData);
              const validatedSoFar = freshStats.valid + freshStats.invalid;
              const totalLeads = freshStats.total;
              const progressPercent = totalLeads > 0 ? (validatedSoFar / totalLeads) * 100 : 0;
              
              console.log(`📊 Progress: ${validatedSoFar}/${totalLeads} (${progressPercent.toFixed(1)}%)`);
              console.log(`📊 Stats breakdown: valid=${freshStats.valid}, invalid=${freshStats.invalid}, pending=${freshStats.pending}`);
              
              setValidationProgress(Math.min(progressPercent, 99));
              setValidationMessage(`Validating emails... (${validatedSoFar} / ${totalLeads} completed)`);
              
              // Check if validation is complete
              if (freshStats.pending === 0 && validatedSoFar > 0) {
                stopPolling();
                setValidationProgress(100);
                setValidationMessage('Validation complete!');
                setValidating(false);
                setSuccess(`Email validation completed! Valid: ${freshStats.valid}, Invalid: ${freshStats.invalid}`);
              }
            }
            
            // Stop polling after 60 minutes (increased from 30)
            if (pollCount > 360) { // 60 minutes * 60 seconds / 10 seconds per poll
              stopPolling();
              setValidating(false);
              setSuccess('Validation is still running in the background. Refresh the page to see final results.');
            }
          } catch (error) {
            console.error('Error polling for updates:', error);
            // Continue polling even on error
          }
        }, 10000); // Poll every 10 seconds
      };
      
      startPolling();
      
    } catch (error: any) {
      console.error('Error starting validation:', error);
      if (error.name !== 'AbortError') {
        setError(error.response?.data?.message || error.message || 'Failed to start email validation');
      }
      setSuccess(null);
      setValidationProgress(0);
      setValidationMessage('');
      setValidating(false);
    }
  };
  
  // Stop polling function
  const stopPolling = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  };

  // Check if validation is running by calling the backend status endpoint
  const checkValidationStatus = async () => {
    try {
      const response = await api.get('/api/admin/email-hub/status');
      
      if (response.data) {
        const status = response.data.status;
        setIsValidationRunning(status.isRunning);
        
        console.log('📊 Validation status:', status);
        console.log(`Process running: ${status.processRunning}, Recent activity: ${status.recentActivity}`);
        
        // Log stuck status for debugging
        if (status.isStuck) {
          console.warn('⚠️ Validation process appears stuck!');
        }
      }
    } catch (error) {
      console.error('Error checking validation status:', error);
    }
  };

  // Resume validation (same as start but with different messaging)
  const resumeValidation = async () => {
    await startValidation();
  };

  // Reset all validation statuses
  const resetValidation = async () => {
    try {
      setResetting(true);
      setResetDialogOpen(false);
      
      await api.post('/api/admin/email-hub/reset');
      await fetchLeads(true); // Show loading when manually refreshing
      
      setSuccess('All validation statuses have been reset to pending!');
      setError(null);
    } catch (error: any) {
      console.error('Error resetting validation:', error);
      setError(error.response?.data?.message || 'Failed to reset validation statuses');
      setSuccess(null);
    } finally {
      setResetting(false);
    }
  };

  // Delete invalid email leads
  const deleteInvalidLeads = async () => {
    try {
      setDeletingInvalid(true);
      await api.delete('/api/admin/email-hub/delete-invalid');
      await fetchLeads(true); // Show loading when manually refreshing
      setDeleteDialogOpen(false);
      setSuccess('Invalid email leads deleted successfully!');
      setError(null);
    } catch (error: any) {
      console.error('Error deleting invalid leads:', error);
      setError(error.response?.data?.message || 'Failed to delete invalid leads');
      setSuccess(null);
    } finally {
      setDeletingInvalid(false);
    }
  };

  // Get status icon
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'valid':
        return <ValidIcon color="success" />;
      case 'invalid':
        return <InvalidIcon color="error" />;
      default:
        return <PendingIcon color="warning" />;
    }
  };

  // Get status chip
  const getStatusChip = (status: string) => {
    switch (status) {
      case 'valid':
        return <Chip icon={<ValidIcon />} label="Valid" color="success" size="small" />;
      case 'invalid':
        return <Chip icon={<InvalidIcon />} label="Invalid" color="error" size="small" />;
      default:
        return <Chip icon={<PendingIcon />} label="Pending" color="warning" size="small" />;
    }
  };

  useEffect(() => {
    console.log('🔍 EmailHub: Component mounted, starting to fetch leads...');
    fetchLeads(true); // Show loading on initial fetch
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Check validation status when leads change
  useEffect(() => {
    checkValidationStatus();
  }, [leads]);
  
  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, []);

  // Only show full-page loading on initial load, not during background updates
  if (loading && leads.length === 0) {
    console.log('🔍 EmailHub: Showing loading spinner');
    return (
      <Box 
        display="flex" 
        flexDirection="column"
        justifyContent="center" 
        alignItems="center" 
        minHeight="400px"
        sx={{
          animation: 'fadeIn 0.5s ease-in',
          '@keyframes fadeIn': {
            from: { opacity: 0, transform: 'translateY(20px)' },
            to: { opacity: 1, transform: 'translateY(0)' },
          }
        }}
      >
        <CircularProgress size={60} thickness={4} />
        <Typography variant="h6" sx={{ mt: 3, fontWeight: 500, color: 'primary.main' }}>
          Loading Email Hub...
        </Typography>
        <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
          Fetching leads from database
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Email Hub
      </Typography>
      
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Manage email validation for all leads across all clients
      </Typography>
      
      {/* Debug info */}
      <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
        <Typography variant="body2">
          Debug Info: Component loaded, User: {user?.username}, Token: {!!sessionStorage.getItem('token') ? 'Yes' : 'No'}
        </Typography>
      </Box>

      {/* Error and Success Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Box sx={{ 
        display: 'flex', 
        gap: 2, 
        mb: 3,
        animation: 'slideIn 0.5s ease-out',
        '@keyframes slideIn': {
          from: { opacity: 0, transform: 'translateY(-20px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        }
      }}>
        <Card sx={{ 
          flex: 1,
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: 6,
          }
        }}>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Total Leads
            </Typography>
            <Typography variant="h4">
              {stats.total}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ 
          flex: 1,
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: 6,
          }
        }}>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Valid Emails
            </Typography>
            <Typography variant="h4" color="success.main">
              {stats.valid}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ 
          flex: 1,
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: 6,
          }
        }}>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Invalid Emails
            </Typography>
            <Typography variant="h4" color="error.main">
              {stats.invalid}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ 
          flex: 1,
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: 6,
          }
        }}>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Pending
            </Typography>
            <Typography variant="h4" color="warning.main">
              {stats.pending}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Background Validation Status */}
      {isValidationRunning && (
        <Card sx={{ 
          mb: 3, 
          bgcolor: 'background.paper',
          boxShadow: 2,
          border: '1px solid',
          borderColor: 'divider'
        }}>
          <CardContent sx={{ py: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={24} />
              <Typography variant="body1" fontWeight="medium" color="text.primary">
                🔄 Email validation is running in the background...
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ mt: 1 }} color="text.secondary">
              ✅ Process is persistent - validation continues even if you close this tab or browser.
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }} color="text.secondary">
              📊 Updates every 10 seconds when you're on this page.
            </Typography>
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Progress: <strong style={{ color: theme.palette.text.primary }}>{stats.valid + stats.invalid} out of {stats.total}</strong> completed
              </Typography>
              <Box sx={{ flex: 1, maxWidth: 300 }}>
                <LinearProgress 
                  variant="determinate" 
                  value={stats.total > 0 ? ((stats.valid + stats.invalid) / stats.total) * 100 : 0}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                    '& .MuiLinearProgress-bar': {
                      bgcolor: 'primary.main'
                    }
                  }}
                />
              </Box>
              <Typography variant="body2" color="text.secondary">
                {stats.total > 0 ? Math.round(((stats.valid + stats.invalid) / stats.total) * 100) : 0}%
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        {!isValidationRunning && (
          <Button
            variant="contained"
            onClick={startValidation}
            disabled={validating || resetting || loading}
            startIcon={validating ? <CircularProgress size={20} /> : <RefreshIcon />}
            sx={{
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: 4,
              }
            }}
          >
            {validating ? 'Validating...' : 'Start Validation'}
          </Button>
        )}
        
        <Button
          variant="outlined"
          color="secondary"
          onClick={() => setResetDialogOpen(true)}
          disabled={validating || resetting || loading}
          startIcon={resetting ? <CircularProgress size={20} /> : <RefreshIcon />}
          sx={{
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-2px)',
            }
          }}
        >
          {resetting ? 'Resetting...' : 'Reset All Tests'}
        </Button>
        
        {stats.invalid > 0 && (
          <Button
            variant="outlined"
            color="error"
            onClick={() => setDeleteDialogOpen(true)}
            disabled={validating || resetting || loading}
            startIcon={<DeleteIcon />}
            sx={{
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
              }
            }}
          >
            Delete Invalid Leads ({stats.invalid})
          </Button>
        )}
      </Box>

      {/* Validation Progress - Only show if manually validating (not background) */}
      {validating && !isValidationRunning && (
        <Card sx={{ mb: 3, bgcolor: 'background.paper', boxShadow: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <CircularProgress size={24} />
              <Typography variant="h6" sx={{ fontWeight: 500 }} color="text.primary">
                {validationMessage || 'Processing...'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <LinearProgress 
                  variant="determinate" 
                  value={validationProgress}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    transition: 'all 0.3s ease',
                  }}
                />
              </Box>
              <Typography variant="body2" sx={{ minWidth: 50, textAlign: 'right', fontWeight: 600 }} color="text.secondary">
                {Math.round(validationProgress)}%
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <TextField
          placeholder="Search leads..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 300 }}
        />
        
        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel>Filter by Status</InputLabel>
          <Select
            value={filterStatus}
            label="Filter by Status"
            onChange={(e) => setFilterStatus(e.target.value as any)}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="valid">Valid</MenuItem>
            <MenuItem value="invalid">Invalid</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Leads Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Company</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Client</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Validated</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredLeads && Array.isArray(filteredLeads) && filteredLeads.map((lead) => (
              <TableRow key={lead._id}>
                <TableCell>
                  {lead.originalData?.['First Name']} {lead.originalData?.['Last Name']}
                </TableCell>
                <TableCell>{lead.originalData?.['Company Name']}</TableCell>
                <TableCell>{lead.originalData?.['Work Email']}</TableCell>
                <TableCell>{lead.originalData?.['Work Phone']}</TableCell>
                <TableCell>{lead.companyName}</TableCell>
                <TableCell>
                  {getStatusChip(lead.emailValidationStatus || 'pending')}
                </TableCell>
                <TableCell>
                  {lead.emailValidationDate 
                    ? new Date(lead.emailValidationDate).toLocaleDateString()
                    : '-'
                  }
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {(!filteredLeads || filteredLeads.length === 0) && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body1" color="text.secondary">
            No leads found matching your criteria
          </Typography>
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon color="error" />
            Delete Invalid Email Leads
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete all {stats.invalid} leads with invalid email addresses? 
            This action cannot be undone and will remove these leads from all client dashboards.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={deleteInvalidLeads}
            color="error"
            variant="contained"
            disabled={deletingInvalid}
          >
            {deletingInvalid ? 'Deleting...' : 'Delete All Invalid'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Validation Dialog */}
      <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)}>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <RefreshIcon color="secondary" />
            Reset All Validation Tests
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography>
            This will reset all email validation statuses to "Pending" for all leads. 
            You'll be able to run a fresh validation test.
          </Typography>
          <Typography sx={{ mt: 2, fontWeight: 500, color: 'warning.main' }}>
            ⚠️ This action will clear all previous validation results.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={resetValidation}
            color="secondary"
            variant="contained"
            disabled={resetting}
          >
            {resetting ? 'Resetting...' : 'Reset All'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmailHub;
